import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import {
  useGuidePricing,
  useGuidePricingHistory,
  useUpdateGuidePricing,
} from '@/features/guide/hooks/useGuidePricing';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';
import { GuideDashboardCard } from '@/features/guide/components/GuideDashboardCard';
import { PricingHistoryTable } from '@/features/guide/components/PricingHistoryTable';
import { GuideGoLiveSetupBanner } from '@/features/guide/components/GuideGoLiveSetupBanner';
import { formatGuidePrice, isGuideMarketplaceLive } from '@/features/guide/utils/pricing';
import { Button } from '@/shared/components/ui';
import { dashboardPanelSx } from '@/shared/styles/surfaces';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'] as const;

const requiredPriceField = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
  z.number({ required_error: 'Required' }).min(0, 'Must be 0 or greater')
);

const schema = z.object({
  hourlyPrice: requiredPriceField,
  fullDayPrice: requiredPriceField,
  currency: z.enum(CURRENCIES),
});

type FormData = z.infer<typeof schema>;

function toFormDefaults(pricing?: {
  hourlyPrice?: number | null;
  fullDayPrice?: number | null;
  currency?: string;
}): Partial<FormData> {
  return {
    hourlyPrice: pricing?.hourlyPrice ?? undefined,
    fullDayPrice: pricing?.fullDayPrice ?? undefined,
    currency: (pricing?.currency as FormData['currency']) ?? 'INR',
  };
}

export function GuidePricingPage() {
  const { data: pricing, isLoading, error } = useGuidePricing();
  const { data: history, isLoading: historyLoading, error: historyError } = useGuidePricingHistory(
    1,
    10
  );
  const updatePricing = useUpdateGuidePricing();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: toFormDefaults(),
  });

  useEffect(() => {
    if (pricing) {
      reset(toFormDefaults(pricing));
    }
  }, [pricing, reset]);

  const onSubmit = async (data: FormData) => {
    await updatePricing.mutateAsync({
      hourlyPrice: data.hourlyPrice,
      fullDayPrice: data.fullDayPrice,
      currency: data.currency,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const lastUpdated = pricing?.updatedAt
    ? new Date(pricing.updatedAt).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const hasConfiguredRates = pricing?.hourlyPrice != null && pricing?.fullDayPrice != null;
  const isLive = isGuideMarketplaceLive(pricing);
  const showGoLiveSetup = !isLive;
  const savedPricing = updatePricing.data;
  const justWentLive =
    updatePricing.isSuccess && isGuideMarketplaceLive(savedPricing ?? pricing);

  return (
    <Box>
      <GuidePageHeader
        title="Pricing"
        subtitle={
          isLive || justWentLive
            ? 'Update your hourly and full-day rates anytime. Travelers see these on your public profile.'
            : 'Set rates above 0 to go live — travelers can’t find or book you until pricing is configured.'
        }
      />

      {showGoLiveSetup && !justWentLive && <GuideGoLiveSetupBanner compact />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pricing. Please try again.
        </Alert>
      )}

      {justWentLive && (
        <Alert severity="success" sx={{ mb: 2 }}>
          You&apos;re live — travelers can now find and book you.
        </Alert>
      )}
      <Grid container spacing={2.5} sx={{ alignItems: 'stretch', mb: 3 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <GuideDashboardCard tone="accent">
            <Typography variant="overline" color="secondary.dark" sx={{ letterSpacing: '0.08em' }}>
              Latest
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, mb: 0.75 }}>
              Current pricing snapshot
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A quick overview of your live rates and recent updates visible to travelers.
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 1.75, height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last updated
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                    {lastUpdated ?? 'Not set yet'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 1.75, height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Times changed
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                    {history?.total ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Live rates
              </Typography>
              {hasConfiguredRates ? (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Hourly
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {formatGuidePrice(pricing.hourlyPrice, pricing.currency)} / hr
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Full day
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {formatGuidePrice(pricing.fullDayPrice, pricing.currency)} / day
                    </Typography>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      pt: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {pricing.currency}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add hourly and full-day rates above 0 to go live and appear in traveler search.
                </Typography>
              )}
            </Paper>
          </GuideDashboardCard>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <GuideDashboardCard component="form" onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Your rates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Both hourly and full-day rates are required so travelers can choose either package.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Hourly rate"
                  type="number"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  {...register('hourlyPrice')}
                  error={Boolean(errors.hourlyPrice)}
                  helperText={errors.hourlyPrice?.message ?? 'Per hour, per traveler'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Full day rate"
                  type="number"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  {...register('fullDayPrice')}
                  error={Boolean(errors.fullDayPrice)}
                  helperText={errors.fullDayPrice?.message ?? 'Typically 8+ hours, per traveler'}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Currency"
                  fullWidth
                  defaultValue="INR"
                  {...register('currency')}
                  error={Boolean(errors.currency)}
                  helperText={errors.currency?.message ?? 'Shown across your public pricing'}
                >
                  {CURRENCIES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {updatePricing.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(updatePricing.error as Error).message || 'Failed to save pricing'}
              </Alert>
            )}

            {updatePricing.isSuccess && !justWentLive && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Pricing saved successfully.
              </Alert>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                intent="primary-gold"
                size="large"
                disableMotion
                disabled={updatePricing.isPending}
                sx={{ minWidth: { xs: '100%', sm: 180 } }}
              >
                {updatePricing.isPending
                  ? 'Saving…'
                  : isLive || justWentLive
                    ? 'Save pricing'
                    : 'Save & go live'}
              </Button>
            </Box>
          </GuideDashboardCard>
        </Grid>
      </Grid>

      <GuideDashboardCard sx={{ height: 'auto' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Pricing history
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A log of every time you updated your rates.
        </Typography>
        <PricingHistoryTable
          items={history?.items ?? []}
          isLoading={historyLoading}
          error={historyError as Error | null}
        />
      </GuideDashboardCard>
    </Box>
  );
}
